import { API } from '@vp/common';
import { defineStore } from 'pinia';
import { ApolloClient, ApolloLink } from '@apollo/client/core/index';
import { useRootStore } from '@/stores/root';
import axios, { type AxiosInstance } from 'axios';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import type { NormalizedCacheObject } from '@apollo/client/core';
import { useLanguageStore } from '@/checkout/languageStore';

const AUTH_TOKEN_KEY = 'vpAuthToken';

export interface ConnectionState {
  apiBase?: string;
  authToken?: string;
}

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

let apiClient: ApolloClient<NormalizedCacheObject> | undefined;
let attachmentsClient: AxiosInstance | undefined;

const authMiddleware = new ApolloLink((operation, forward) => {
  const connectionStore = useConnectionStore();
  const authToken = connectionStore.authToken;
  if (!authToken) {
    return forward(operation);
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: `Bearer ${authToken}`,
    },
  }));

  return forward(operation);
});

const langMiddleware = new ApolloLink((operation, forward) => {
  const languageStore = useLanguageStore();
  const lang = languageStore.current;

  if (!lang) {
    return forward(operation);
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'X-VP-Locale': lang,
    },
  }));

  return forward(operation);
});

export const useConnectionStore = defineStore('connection', {
  state: (): ConnectionState => {
    const authToken = typeof window !== 'undefined' ? (localStorage.getItem(AUTH_TOKEN_KEY) || undefined) : undefined;
    return {
      authToken,
    };
  },
  getters: {
    apiClient(): ApolloClient<NormalizedCacheObject> | undefined {
      if (!this.apiBase) {
        throw new Error('apiBase is not set');
      }

      if (typeof window !== 'undefined') {
        // We're in browser, we can cache the client
        if (!apiClient) {
          apiClient = API.Client.createApiClient({ apiBase: this.apiBase, middleware: [authMiddleware, langMiddleware] });
        }
        return apiClient;
      } else {
        // In SSR, always create a new instance so it doesn't leak between requests
        return API.Client.createApiClient({ apiBase: this.apiBase, middleware: [authMiddleware, langMiddleware] });
      }
    },
    attachmentsClient(): AxiosInstance | undefined {
      return attachmentsClient;
    },
  },
  actions: {
    init(options: API.Client.InitClientParams) {
      this.apiBase = options.apiBase;
    },

    initAttachmentsClient() {
      if (attachmentsClient) {
        throw new Error('uploadClient is already initialized');
      }

      const rootStore = useRootStore();
      const base = `${rootStore.apiBase}/attachments`;

      attachmentsClient = axios.create({
        baseURL: base,
      });
    },

    setAuthToken(token: string) {
      this.authToken = token;
      if (typeof window !== 'undefined') {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
      }
    },
  },
});
